import { computed } from 'vue'

/**
 * Встроенный режим: страница открыта внутри нативного WebView или в iframe.
 *
 * Режим включается ТОЛЬКО query-параметрами — теми, которыми управляем мы и
 * встраивающая сторона:
 *
 *   ?webview=1  — скрыть шапку и футер.
 *   ?noNav=1    — не выполнять внутренние переходы роутера; вместо перехода
 *                 наружу уходит событие, и куда вести пользователя решает
 *                 контейнер.
 *
 * У первого есть синоним ?embedded=1 — под этим именем параметр попал в контракт,
 * отданный мобильной команде, и ломать его нельзя. Имена равнозначны, достаточно
 * любого. Регистр значения не имеет (см. readQuery).
 *
 * АВТООПРЕДЕЛЕНИЯ НЕТ — решение бизнеса. Раньше присутствие объекта
 * window.flutter_inappwebview само включало скрытие обвязки; теперь нативное
 * приложение, как и любой другой контейнер, обязано передать параметр явно.
 *
 * Смысл ограничения: режим включается ровно тогда, когда его включили осознанно,
 * и это проверяется открытием ссылки в обычном браузере. Ни одна сборка чужого
 * приложения не может получить встроенный вид случайно, просто потому что
 * открыла страницу в вебвью.
 *
 * Цена: приложение обязано дописать ?webview=1 в ссылку на курс — без этого
 * встроенный режим у него не включится вовсе (см. course_page_url.dart в
 * flutter-web-ui).
 *
 * ПОЧЕМУ webview ЗАПОМИНАЕТСЯ, А noNav НЕТ. Внутренние переходы теряют query,
 * поэтому webview приходится запоминать: иначе на следующем экране вернулась бы
 * шапка ЛК посреди приложения.
 *
 * С noNav такой задачи нет. Он отключает ровно те переходы, которыми можно уйти
 * со страницы курса (все они идут через navigateOrNotify), — значит на другой
 * странице кабинета пользователь с этим флагом просто не окажется, и применять
 * там нечего. А если флага нет, переходы работают и запоминать тем более нечего.
 *
 * Единственный способ уйти со страницы при noNav — внешняя форма оплаты, но это
 * другой домен, где наши флаги не действуют в принципе.
 *
 * Для iframe флаги независимы: страницу можно встроить с работающей навигацией
 * (?embedded=1) или запретить переходы, не скрывая хром (?noNav=1). Скрытие
 * хрома и передача навигации контейнеру — разные решения встраивающей стороны.
 *
 * ГДЕ РЕЖИМ ДЕЙСТВУЕТ. Параметр из адреса запоминается на сессию вкладки, иначе
 * он терялся бы на первом же внутреннем переходе (router.push не сохраняет
 * query). Без этого возникала дыра: обвязку скрыли, пользователь нажал «Перейти
 * в курс» — и попал на календарь с полноценной шапкой ЛК посреди приложения.
 *
 * Флаг действует на всём ЛК, и проверку делают сами компоненты обвязки —
 * headers/mobile, headers/desktop, AsFooter, AsSpacer. Все 38 вьюх с обвязкой
 * используют эти четыре компонента, поэтому вьюхи о встроенном режиме не знают
 * вовсе: новая страница получает правильное поведение сама, без правок.
 *
 * Плата — флаг переживает выход из вебвью: открыв ЛК в той же вкладке браузера,
 * пользователь увидит его без шапки до конца сессии вкладки. Это принято
 * сознательно: вкладка вебвью и вкладка браузера — разные вкладки, а сценарий
 * «вышел из приложения в браузер той же вкладкой» не реален.
 */
const WEBVIEW_QUERY_KEYS = ['webview', 'embedded']
const NO_NAV_QUERY_KEY = 'noNav'

const WEBVIEW_STORAGE_KEY = 'as:webview'

/**
 * Разбирает query-строку напрямую, а не через vue-router: вызывается в том
 * числе до инициализации роутера.
 *
 * Имя параметра ищем без учёта регистра: ссылки на встроенные страницы
 * набирают руками и складывают в конфиги, а noNav с заглавной буквой посреди
 * слова легко написать как nonav — и молча получить работающую навигацию
 * вместо отключённой. Значение при этом строгое: включает только 1 или true.
 */
function readQuery (key: string): boolean {
  const wanted = key.toLowerCase()

  for (const [name, value] of new URLSearchParams(window.location.search)) {
    if (name.toLowerCase() === wanted) {
      return value === '1' || value === 'true'
    }
  }

  return false
}

/**
 * sessionStorage обёрнут в try/catch: в приватном режиме Safari обращение к
 * нему бросает. Недоступное хранилище не должно ронять страницу — режим просто
 * проживёт до первого перехода, как если бы списка не было.
 */
function remember (key: string): void {
  try {
    window.sessionStorage.setItem(key, '1')
  } catch (e) {
    // хранилище недоступно — молча работаем без запоминания
  }
}

function wasRemembered (key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1'
  } catch (e) {
    return false
  }
}

/**
 * Читает флаг из адреса и фиксирует его на сессию вкладки.
 */
function readAndRemember (queryKeys: string[], storageKey: string): boolean {
  if (queryKeys.some(readQuery)) {
    remember(storageKey)
    return true
  }

  return wasRemembered(storageKey)
}

/**
 * Признак встроенного режима без учёта текущей страницы: параметр в адресе,
 * нативный контейнер или запомненный флаг.
 *
 * Параметр из адреса тут же фиксируется на сессию — это единственное место,
 * где флаг записывается.
 */
function isWebviewSession (): boolean {
  return readAndRemember(WEBVIEW_QUERY_KEYS, WEBVIEW_STORAGE_KEY)
}

export function isEmbedded (): boolean {
  return isWebviewSession()
}

/**
 * Внутренние переходы отданы контейнеру.
 *
 * Сознательно БЕЗ автодетекта нативного контейнера — см. пояснение в шапке
 * файла: присутствие моста не означает, что события кто-то обрабатывает, а
 * отключённая навигация без обработчика оставляет мёртвые кнопки.
 */
export function isNavBlocked (): boolean {
  return readQuery(NO_NAV_QUERY_KEY)
}

export default function useEmbedMode () {
  const embedded = computed(() => isEmbedded())
  const navBlocked = computed(() => isNavBlocked())

  return {
    isEmbedded: embedded,
    isNavBlocked: navBlocked,
    // Обвязка страницы — шапка, футер и отступ под них. Во встроенном режиме
    // её рисует контейнер, а не мы: остаётся только контент.
    showLayout: computed(() => !embedded.value)
  }
}
